import { useState, useCallback } from 'react';

interface UseRecordAudioReturn {
  isRecording: boolean;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<Blob | null>;
  recordingError: string | null;
}

// Audio recording configuration for better quality
const AUDIO_CONFIG = {
  mimeType: 'audio/webm;codecs=opus', // Try opus codec for better quality
  audioBitsPerSecond: 128000, // Higher bitrate for better quality
  timeslice: 500 // Collect data more frequently
};

const useRecordAudio = (): UseRecordAudioReturn => {
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const [audioChunks, setAudioChunks] = useState<Blob[]>([]);

  // Check if the browser supports the specified mime type
  const getSupportedMimeType = () => {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/mp4',
      'audio/mpeg'
    ];
    
    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        console.log(`Browser supports audio format: ${type}`);
        return type;
      }
    }
    
    console.warn('None of the preferred audio formats are supported, using default');
    return '';
  };

  const startRecording = useCallback(async () => {
    setRecordingError(null);
    setAudioChunks([]);

    try {
      // Request audio with specific constraints for better quality
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000
        }
      });
      
      // Get supported mime type
      const mimeType = getSupportedMimeType();
      
      // Create recorder with best supported options
      const recorderOptions: MediaRecorderOptions = {
        audioBitsPerSecond: AUDIO_CONFIG.audioBitsPerSecond
      };
      
      if (mimeType) {
        recorderOptions.mimeType = mimeType;
      }
      
      console.log('Creating MediaRecorder with options:', recorderOptions);
      const recorder = new MediaRecorder(stream, recorderOptions);
      
      recorder.ondataavailable = (event) => {
        console.log('Data available event triggered', event.data.size);
        if (event.data.size > 0) {
          setAudioChunks((chunks) => {
            const newChunks = [...chunks, event.data];
            console.log(`Audio chunks updated: now have ${newChunks.length} chunks`);
            return newChunks;
          });
        }
      };

      // Start recording with configured timeslice
      recorder.start(AUDIO_CONFIG.timeslice);
      console.log(`MediaRecorder started with ${AUDIO_CONFIG.timeslice}ms timeslice`);
      
      // Log recorder state and settings
      console.log(`MediaRecorder state: ${recorder.state}`);
      console.log(`MediaRecorder mimeType: ${recorder.mimeType}`);
      
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (error) {
      setRecordingError('Microphone access denied or not available');
      console.error('Error accessing microphone:', error);
    }
  }, []);

  const stopRecording = useCallback(async (): Promise<Blob | null> => {
    return new Promise((resolve) => {
      if (!mediaRecorder || mediaRecorder.state === 'inactive') {
        console.log('MediaRecorder not active or null when trying to stop');
        setIsRecording(false);
        resolve(null);
        return;
      }

      // Request a final dataavailable event before stopping
      mediaRecorder.requestData();

      mediaRecorder.onstop = () => {
        const tracks = mediaRecorder.stream.getTracks();
        tracks.forEach(track => track.stop());
        
        console.log(`Creating blob from ${audioChunks.length} audio chunks`);
        if (audioChunks.length > 0) {
          // Use the mime type from the recorder for the blob
          const audioBlob = new Blob(audioChunks, { type: mediaRecorder.mimeType || 'audio/webm' });
          console.log(`Created blob of size ${audioBlob.size} bytes, type: ${audioBlob.type}`);
          
          // Verify the blob has content
          if (audioBlob.size < 100) {
            console.warn('Warning: Audio blob is very small, may not contain valid audio data');
          }
          
          setIsRecording(false);
          resolve(audioBlob);
        } else {
          console.error('No audio chunks collected during recording');
          setIsRecording(false);
          resolve(null);
        }
      };

      mediaRecorder.stop();
      console.log('MediaRecorder stop requested');
    });
  }, [mediaRecorder, audioChunks]);

  return {
    isRecording,
    startRecording,
    stopRecording,
    recordingError
  };
};

export default useRecordAudio; 